/**
 * Unikulma bed configurator — visibility, live price preview, line-item properties.
 * Authoritative cart price comes later from Cart Transform; this is preview only.
 */
(() => {
  const MASSAGE_OPTS = [
    { handle: 'none', name: 'Ei hierontaa', price: 0 },
    { handle: 'with', name: 'Hieronnalla', price: null },
  ];

  // Same chevron path and class the theme uses in its variant picker dropdowns
  const CHEVRON_SVG = `
    <svg class="variant-picker-dropdown__chevron sanky-config__select-arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M9.5 7L14.5 12L9.5 17" stroke="currentColor" stroke-width="1.5"/>
    </svg>`;

  class SankyConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const raw = this.querySelector('[data-sanky-config]');
      if (!raw) return;

      try {
        this.config = JSON.parse(raw.textContent);
      } catch (e) {
        console.error('[sanky-configurator] Invalid config JSON', e);
        return;
      }

      this.state = {
        mattress: null,
        mattressLeft: null,
        mattressRight: null,
        fabric: null,
        legs: null,
        topperSplit: null,
        springSplit: null,
        remotePocket: null,
        massage: null,
        massageLeft: null,
        massageRight: null,
      };

      this.groupsEl = this.querySelector('[data-sanky-groups]');
      this.priceEl = this.querySelector('[data-sanky-price]');
      this.priceValueEl = this.querySelector('[data-sanky-price-value]');

      this._normalizeCatalog();
      this._buildUi();
      this._setDefaults();
      this._sync();
    }

    _normalizeCatalog() {
      const sortBy = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0);
      this.config.fabrics = (this.config.fabrics || []).slice().sort(sortBy);
      this.config.legs = (this.config.legs || []).slice().sort(sortBy);
      this.config.mattresses = (this.config.mattresses || []).slice().sort(sortBy);

      const s = this.config.settings || {};
      if (typeof s.optionInfos === 'string') {
        try { s.optionInfos = JSON.parse(s.optionInfos); } catch (_) { s.optionInfos = {}; }
      }
      s.optionInfos = s.optionInfos && typeof s.optionInfos === 'object' && !Array.isArray(s.optionInfos)
        ? s.optionInfos
        : {};
      this.config.settings = s;
      this.ui = this.config.ui || {};

      // Pricing slice is null until the app has synced prices onto the product
      const pricing = this.config.pricing || {};
      pricing.mattresses = pricing.mattresses || {};
      this.config.pricing = pricing.jousitus ? pricing : null;

      // The width that splits the bed into two sleeping sides has to be the one
      // the pricing master uses, or the preview and the cart disagree
      s.sideThresholdCm = Number(this.config.pricing?.thresholdCm) || 160;

      const p = this.config.product || {};
      p.widthCm = this._parseWidthCm(p);
      p.basePrice = Number(p.basePrice) || 0;
      this.config.product = p;
    }

    /**
     * Width decides the whole "one mattress vs. two sleeping sides" logic, so accept every
     * shape the metafield can arrive in: number, "160", "160 cm", dimension object
     * ({ value, unit }). Falls back to the size text / product title ("180 x 200 cm").
     * Returns 0 when unknown, which safely renders the single-mattress (narrow) UI.
     */
    _parseWidthCm(product) {
      const raw = product.widthCm;
      const firstNumber = (value) => {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        if (typeof value === 'object') return Number(value.value) || 0;
        const match = String(value).replace(',', '.').match(/\d+(\.\d+)?/);
        return match ? Number(match[0]) : 0;
      };

      const direct = firstNumber(raw);
      if (direct) return direct;

      const sizeText = `${product.bedSize || ''} ${product.title || ''}`;
      const size = sizeText.match(/(\d{2,3})\s*[x×]\s*(\d{2,3})/i);
      return size ? Number(size[1]) : 0;
    }

    _rules() {
      const { mallisto, tuotetyyppi, widthCm } = this.config.product;
      const threshold = this.config.settings.sideThresholdCm;
      const wide = widthCm >= threshold;
      const isJoustin = tuotetyyppi === 'Joustinpatja';
      const isRunko = tuotetyyppi === 'Runkopatja';
      const isJenkki = tuotetyyppi === 'Jenkkisänky';
      const isMoottori = tuotetyyppi === 'Moottorisänky';
      const showMassage = isMoottori && mallisto === 'Premium';

      return {
        mallisto,
        tuotetyyppi,
        wide,
        widthCm,
        threshold,
        sides: wide ? 2 : 1,
        showSingleMattress: !wide,
        showSplitMattress: wide,
        showFabric: true,
        showLegs: !isJoustin,
        showTopperSplit: wide && (isRunko || isJenkki || isMoottori),
        showSpringSplit: wide && (isJenkki || isMoottori),
        showRemote: isMoottori,
        showMassage,
        showMassageSplit: showMassage && wide,
        massageIncluded: isMoottori && mallisto === 'Lux',
      };
    }

    _copy(rules) {
      const topperName = rules.mallisto === 'Core' ? 'Safe-petauspatja' : 'Tec-petauspatja';
      const isJoustin = rules.tuotetyyppi === 'Joustinpatja';
      return {
        mattress: 'Valitse jousitus',
        mattressLeft: 'Valitse vasen jousitus',
        mattressRight: 'Valitse oikea jousitus',
        springSplit: 'Yhtenäinen vai erillinen joustinpatja',
        topperSplit: 'Yhtenäinen vai erillinen petauspatja',
        fabric: isJoustin ? 'Valitse verhoilu' : 'Valitse kuosi',
        fabricInfoKey: isJoustin ? 'upholstery' : 'fabric',
        legs: 'Valitse jalat',
        remote: rules.wide ? 'Valitse kaukosäädinpussit' : 'Valitse kaukosäädinpussi',
        massage: 'Valitse hieronta',
        massageLeft: 'Valitse vasemman puolen hieronta',
        massageRight: 'Valitse oikean puolen hieronta',
        springOpts: [
          { handle: 'unified', name: 'Yhtenäinen joustinpatja' },
          { handle: 'split', name: 'Kaksi erillistä joustinpatjaa' },
        ],
        topperOpts: [
          { handle: 'unified', name: `Yhtenäinen ${topperName}` },
          { handle: 'split', name: `Kaksi erillistä ${topperName}` },
        ],
        remoteOpts: rules.wide
          ? [
              { handle: 'narrow', name: 'Kapeat' },
              { handle: 'wide', name: 'Leveät' },
              { handle: 'none', name: 'Ei kaukosäädinpusseja' },
            ]
          : [
              { handle: 'narrow', name: 'Kapea' },
              { handle: 'wide', name: 'Leveä' },
              { handle: 'none', name: 'Ei kaukosäädinpussia' },
            ],
      };
    }

    _mattressesForMallisto() {
      const { mallisto } = this.config.product;
      return this.config.mattresses.filter((m) => {
        const scope = m.scope || '';
        return scope === 'Kaikki' || scope === mallisto;
      });
    }

    _buildUi() {
      const rules = this._rules();
      const copy = this._copy(rules);
      const mattresses = this._mattressesForMallisto();
      const tabs = [];

      const mattressGroups = [];
      if (rules.showSingleMattress) {
        mattressGroups.push(this._selectGroupHtml('mattress', copy.mattress, mattresses, { withPrice: true, infoKey: 'mattress' }));
      }
      if (rules.showSplitMattress) {
        mattressGroups.push(`
          <div class="sanky-config__grid-2">
            ${this._selectGroupHtml('mattressLeft', copy.mattressLeft, mattresses, { withPrice: true, infoKey: 'mattressLeft' })}
            ${this._selectGroupHtml('mattressRight', copy.mattressRight, mattresses, { withPrice: true, infoKey: 'mattressRight' })}
          </div>`);
      }
      if (rules.showSpringSplit) {
        mattressGroups.push(this._groupHtml('springSplit', copy.springSplit, copy.springOpts));
      }
      if (rules.showTopperSplit) {
        mattressGroups.push(this._groupHtml('topperSplit', copy.topperSplit, copy.topperOpts));
      }
      if (mattressGroups.length) {
        tabs.push({ id: 'patja', label: 'Patja', html: mattressGroups.join('') });
      }

      if (rules.showFabric) {
        tabs.push({
          id: 'verhoilu',
          label: 'Verhoilu',
          html: this._tieredSwatchGroupsHtml('fabric', copy.fabric, this.config.fabrics, 'kuosi', copy.fabricInfoKey),
        });
      }
      if (rules.showLegs) {
        tabs.push({
          id: 'jalat',
          label: 'Jalat',
          html: this._tieredSwatchGroupsHtml('legs', copy.legs, this.config.legs, 'jalat', 'legs'),
        });
      }

      const extraGroups = [];
      if (rules.showRemote) {
        extraGroups.push(this._selectGroupHtml('remotePocket', copy.remote, copy.remoteOpts, { infoKey: 'remote' }));
      }
      if (rules.showMassage) {
        // Hieronta is priced per massaged side, so the label is the same in both layouts
        const price = Math.round(Number(this.config.pricing?.hieronta?.price) || 0);
        const opts = MASSAGE_OPTS.map((o) => ({
          ...o,
          name: o.handle === 'with' && price ? `Hieronnalla (+${price} €)` : o.name,
        }));

        if (rules.showMassageSplit) {
          extraGroups.push(this._selectGroupHtml('massageLeft', copy.massageLeft, opts, { infoKey: 'massageLeft' }));
          extraGroups.push(this._selectGroupHtml('massageRight', copy.massageRight, opts, { infoKey: 'massageRight' }));
        } else {
          extraGroups.push(this._selectGroupHtml('massage', copy.massage, opts, { infoKey: 'massage' }));
        }
      }
      if (extraGroups.length) {
        tabs.push({ id: 'lisat', label: 'Lisävarusteet', html: extraGroups.join('') });
      }

      this.tabs = tabs;
      this.activeTab = tabs[0]?.id || null;

      if (!tabs.length) {
        this.groupsEl.innerHTML = '';
        return;
      }

      const showTabBar = tabs.length > 1;
      const tabBar = showTabBar
        ? `<div class="sanky-config__tabs" role="tablist" aria-label="Sängyn räätälöinti">
            ${tabs.map((tab, i) => `
              <button
                type="button"
                class="sanky-config__tab heading-font-family${i === 0 ? ' is-active' : ''}"
                role="tab"
                id="sanky-tab-${tab.id}"
                aria-selected="${i === 0 ? 'true' : 'false'}"
                aria-controls="sanky-panel-${tab.id}"
                aria-label="Vaihe ${i + 1}/${tabs.length}: ${this._esc(tab.label)}"
                tabindex="${i === 0 ? '0' : '-1'}"
                data-sanky-tab="${tab.id}"
              >
                <span class="sanky-config__tab-index" aria-hidden="true">${i + 1}</span>
                <span class="sanky-config__tab-label">${this._esc(tab.label)}</span>
              </button>
            `).join('')}
          </div>`
        : '';

      const panels = tabs.map((tab, i) => `
        <div
          class="sanky-config__panel${i === 0 ? ' is-active' : ''}"
          role="tabpanel"
          id="sanky-panel-${tab.id}"
          aria-labelledby="sanky-tab-${tab.id}"
          data-sanky-panel="${tab.id}"
          ${i === 0 ? '' : 'hidden'}
        >
          <div class="product-variant-picker__list sanky-config__panel-groups">
            ${tab.html}
          </div>
        </div>
      `).join('');

      const nav = showTabBar
        ? `<div class="sanky-config__nav">
            <button type="button" class="sanky-config__nav-btn" data-sanky-nav="prev" hidden></button>
            <button type="button" class="sanky-config__nav-btn sanky-config__nav-btn--primary" data-sanky-nav="next"></button>
          </div>`
        : '';

      this.groupsEl.innerHTML = `${tabBar}${panels}${nav}`;
      this.groupsEl.classList.remove('product-variant-picker__list');
      this._ensureInfoTip();

      // Handles both radio pills and the mattress dropdowns
      this.groupsEl.addEventListener('change', (e) => {
        const field = e.target.closest('[data-sanky-field]');
        if (!field) return;
        if (field.type === 'radio') {
          if (!field.checked) return;
          this.state[field.dataset.sankyField] = field.value;
          this._refreshSelected(field.name);
        } else {
          this.state[field.dataset.sankyField] = field.value;
        }
        this._sync();
      });

      // Hovering or focusing a swatch previews its name in the group heading
      const previewSwatchName = (e) => {
        const swatch = e.target.closest('[data-sanky-swatch-name]');
        if (!swatch) return;
        const field = swatch.previousElementSibling?.dataset.sankyField;
        const target = field && this.groupsEl.querySelector(`[data-sanky-selection="${field}"]`);
        if (!target) return;
        target.textContent = swatch.dataset.sankySwatchName;
        target.classList.add('is-preview');
      };
      this.groupsEl.addEventListener('mouseover', previewSwatchName);
      this.groupsEl.addEventListener('focusin', previewSwatchName);
      this.groupsEl.addEventListener('mouseout', () => this._updateSelectionLabels());
      this.groupsEl.addEventListener('focusout', () => this._updateSelectionLabels());

      this.groupsEl.addEventListener('keydown', (e) => {
        const tabBtn = e.target.closest('[data-sanky-tab]');
        if (!tabBtn) return;
        const keys = { ArrowRight: 1, ArrowLeft: -1 };
        const step = keys[e.key];
        if (step === undefined && e.key !== 'Home' && e.key !== 'End') return;
        e.preventDefault();
        const idx = this.tabs.findIndex((t) => t.id === this.activeTab);
        let nextIdx = idx;
        if (e.key === 'Home') nextIdx = 0;
        else if (e.key === 'End') nextIdx = this.tabs.length - 1;
        else nextIdx = (idx + step + this.tabs.length) % this.tabs.length;
        this._activateTab(this.tabs[nextIdx].id, { focus: true });
      });

      this.groupsEl.addEventListener('click', (e) => {
        const infoBtn = e.target.closest('[data-sanky-info]');
        if (infoBtn) {
          this._openInfo(infoBtn.dataset.sankyInfo, infoBtn);
          return;
        }
        const tabBtn = e.target.closest('[data-sanky-tab]');
        if (tabBtn) {
          this._activateTab(tabBtn.dataset.sankyTab);
          return;
        }
        const navBtn = e.target.closest('[data-sanky-nav]');
        if (!navBtn) return;
        const idx = this.tabs.findIndex((t) => t.id === this.activeTab);
        if (navBtn.dataset.sankyNav === 'next' && idx < this.tabs.length - 1) {
          this._activateTab(this.tabs[idx + 1].id);
        }
        if (navBtn.dataset.sankyNav === 'prev' && idx > 0) {
          this._activateTab(this.tabs[idx - 1].id);
        }
      });

      this._updateNav();
    }

    _activateTab(tabId, { focus = false } = {}) {
      if (!tabId || tabId === this.activeTab) return;
      this._closeInfo();
      this.activeTab = tabId;

      this.groupsEl.querySelectorAll('[data-sanky-tab]').forEach((btn) => {
        const active = btn.dataset.sankyTab === tabId;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        // Roving tabindex: only the selected tab is in the tab order
        btn.setAttribute('tabindex', active ? '0' : '-1');
        if (active) {
          if (focus) btn.focus();
          // Keep the selected step visible when the tab bar scrolls sideways on mobile
          if (btn.parentElement.scrollWidth > btn.parentElement.clientWidth) {
            btn.scrollIntoView({ inline: 'nearest', block: 'nearest' });
          }
        }
      });

      this.groupsEl.querySelectorAll('[data-sanky-panel]').forEach((panel) => {
        const active = panel.dataset.sankyPanel === tabId;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      this._updateNav();
    }

    _updateNav() {
      const prev = this.groupsEl.querySelector('[data-sanky-nav="prev"]');
      const next = this.groupsEl.querySelector('[data-sanky-nav="next"]');
      if (!prev || !next || !this.tabs?.length) return;
      const idx = this.tabs.findIndex((t) => t.id === this.activeTab);
      prev.hidden = idx <= 0;
      next.hidden = idx >= this.tabs.length - 1;
      if (!prev.hidden) prev.textContent = `← ${this.ui.previousChoice || 'Edellinen valinta'}`;
      if (!next.hidden) {
        const step = (this.tabs[idx + 1].label || '').toLocaleLowerCase('fi-FI');
        const template = this.ui.selectNext || 'Valitse __STEP__';
        next.textContent = `${template.replace('__STEP__', step)} →`;
      }
    }

    // Writes the chosen option name into the group headings, e.g. "Kangas: Fjord Black"
    _updateSelectionLabels() {
      const sources = {
        fabric: this.config.fabrics,
        legs: this.config.legs,
      };
      this.groupsEl.querySelectorAll('[data-sanky-selection]').forEach((el) => {
        const field = el.dataset.sankySelection;
        const name = this._findByHandle(sources[field], this.state[field])?.name;
        el.textContent = name || '—';
        el.classList.remove('is-preview');
      });
    }

    _refreshSelected(inputName) {
      this.groupsEl.querySelectorAll(`input[name="${inputName}"]`).forEach((input) => {
        const pill = input.nextElementSibling;
        if (!pill || !pill.classList.contains('pill')) return;
        pill.classList.toggle('selected', input.checked);
        if (input.checked) pill.setAttribute('tabindex', '0');
        else pill.removeAttribute('tabindex');
      });
    }

    _hintHtml(text) {
      return `<p class="sanky-config__hint">${this._esc(text)}</p>`;
    }

    _infoFor(key) {
      return this.config.settings.optionInfos?.[key] || null;
    }

    _hasInfo(key) {
      const info = this._infoFor(key);
      return Boolean(info && String(info.body || '').trim());
    }

    _infoButtonHtml(infoKey, title) {
      if (!infoKey || !this._hasInfo(infoKey)) return '';
      const label = this._esc(`${this.ui.infoDialog || 'Lisätietoja'}: ${title || ''}`);
      return `
        <button type="button" class="sanky-config__info-btn" data-sanky-info="${this._esc(infoKey)}" aria-label="${label}" aria-expanded="false">
          <svg class="sanky-config__info-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <path d="M12 11.2v5.2M12 7.6h.01" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </button>`;
    }

    _labelRowHtml(title, infoKey, forId = null) {
      const label = forId
        ? `<label class="heading-font-family" for="${forId}">${this._esc(title)}</label>`
        : `<span class="heading-font-family">${this._esc(title)}</span>`;
      return `<div class="product-variant-picker__option-label">${label}${this._infoButtonHtml(infoKey, title)}</div>`;
    }

    _supportsPopover() {
      return typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;
    }

    _isInfoOpen() {
      if (!this.infoEl) return false;
      if (this._supportsPopover()) return this.infoEl.matches(':popover-open');
      return this.infoEl.classList.contains('is-open');
    }

    _ensureInfoTip() {
      if (this.infoEl) return;
      this.insertAdjacentHTML('beforeend', `
        <div class="sanky-config__info-tip" popover="auto" data-sanky-info-tip>
          <span class="sanky-config__info-tip-arrow" aria-hidden="true"></span>
          <div class="sanky-config__info-tip-body rte" data-sanky-info-body></div>
        </div>`);
      this.infoEl = this.querySelector('[data-sanky-info-tip]');
      this.infoEl.addEventListener('toggle', (e) => {
        if (e.newState === 'closed') {
          this._infoDismissedAt = Date.now();
          this._infoDismissedFrom = this._infoAnchor;
          this._teardownInfo();
        }
      });
    }

    _openInfo(key, button) {
      const info = this._infoFor(key);
      if (!info || !button) return;
      this._ensureInfoTip();

      if (this._infoDismissedAt && Date.now() - this._infoDismissedAt < 400 && this._infoDismissedFrom === button) {
        this._infoDismissedAt = 0;
        return;
      }

      if (this._isInfoOpen() && this._infoAnchor === button) {
        this._closeInfo();
        return;
      }

      this._fillInfo(info);
      this._setInfoAnchor(button);

      if (this._isInfoOpen()) {
        this._positionInfo();
        return;
      }

      this.infoEl.style.visibility = 'hidden';
      if (this._supportsPopover() && typeof this.infoEl.showPopover === 'function') {
        try {
          this.infoEl.showPopover({ source: button });
        } catch (_) {
          this.infoEl.showPopover();
        }
      } else {
        this.infoEl.classList.add('is-open');
      }

      this._positionInfo();
      this.infoEl.style.visibility = '';
      this._bindInfoPositioning();
    }

    _fillInfo(info) {
      const bodyEl = this.infoEl.querySelector('[data-sanky-info-body]');
      if (bodyEl) bodyEl.innerHTML = this._formatInfoBody(info.body);
    }

    _setInfoAnchor(button) {
      if (this._infoAnchor && this._infoAnchor !== button) {
        this._infoAnchor.setAttribute('aria-expanded', 'false');
      }
      this._infoAnchor = button;
      button.setAttribute('aria-expanded', 'true');
    }

    _closeInfo() {
      if (!this.infoEl || !this._isInfoOpen()) {
        this._teardownInfo();
        return;
      }
      if (this._supportsPopover() && typeof this.infoEl.hidePopover === 'function') {
        try { this.infoEl.hidePopover(); } catch (_) { /* already closed */ }
        return;
      }
      this.infoEl.classList.remove('is-open');
      this._teardownInfo();
    }

    _teardownInfo() {
      this._unbindInfoPositioning();
      if (this.infoEl) this.infoEl.style.visibility = '';
      if (this._infoAnchor) this._infoAnchor.setAttribute('aria-expanded', 'false');
      this._infoAnchor = null;
      this._onDocPointerDown && document.removeEventListener('pointerdown', this._onDocPointerDown, true);
      this._onDocKeydown && document.removeEventListener('keydown', this._onDocKeydown);
      this._onDocPointerDown = null;
      this._onDocKeydown = null;
    }

    _bindInfoPositioning() {
      this._unbindInfoPositioning();
      this._onInfoReposition = () => this._positionInfo();
      window.addEventListener('resize', this._onInfoReposition);
      window.addEventListener('scroll', this._onInfoReposition, true);
      window.visualViewport?.addEventListener('resize', this._onInfoReposition);
      window.visualViewport?.addEventListener('scroll', this._onInfoReposition);

      if (!this._supportsPopover()) {
        this._onDocPointerDown = (e) => {
          if (this.infoEl.contains(e.target) || this._infoAnchor?.contains(e.target)) return;
          this._closeInfo();
        };
        this._onDocKeydown = (e) => {
          if (e.key === 'Escape') this._closeInfo();
        };
        document.addEventListener('pointerdown', this._onDocPointerDown, true);
        document.addEventListener('keydown', this._onDocKeydown);
      }
    }

    _unbindInfoPositioning() {
      if (!this._onInfoReposition) return;
      window.removeEventListener('resize', this._onInfoReposition);
      window.removeEventListener('scroll', this._onInfoReposition, true);
      window.visualViewport?.removeEventListener('resize', this._onInfoReposition);
      window.visualViewport?.removeEventListener('scroll', this._onInfoReposition);
      this._onInfoReposition = null;
    }

    _positionInfo() {
      const pop = this.infoEl;
      const btn = this._infoAnchor;
      if (!pop || !btn || !this._isInfoOpen()) return;

      const gap = 10;
      const margin = 12;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const btnRect = btn.getBoundingClientRect();
      const popW = pop.offsetWidth;
      const popH = pop.offsetHeight;

      let left = btnRect.left + btnRect.width / 2 - popW / 2;
      left = Math.max(margin, Math.min(left, vw - popW - margin));

      const spaceBelow = vh - btnRect.bottom - gap - margin;
      const spaceAbove = btnRect.top - gap - margin;
      let placement = 'bottom';
      let top = btnRect.bottom + gap;

      if (popH > spaceBelow && spaceAbove > spaceBelow) {
        placement = 'top';
        top = Math.max(margin, btnRect.top - popH - gap);
      } else if (top + popH > vh - margin) {
        top = Math.max(margin, vh - popH - margin);
      }

      const arrowX = Math.max(16, Math.min(btnRect.left + btnRect.width / 2 - left, popW - 16));
      pop.dataset.placement = placement;
      pop.style.top = `${Math.round(top)}px`;
      pop.style.left = `${Math.round(left)}px`;
      pop.style.setProperty('--sanky-arrow-x', `${Math.round(arrowX)}px`);
    }

    _formatInfoBody(body) {
      const text = String(body || '').trim();
      if (!text) return '';
      if (/<[a-z][\s\S]*>/i.test(text)) return this._sanitizeHtml(text);
      return `<p>${this._esc(text).replace(/\n/g, '<br>')}</p>`;
    }

    _sanitizeHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      tmp.querySelectorAll('script,style,iframe,object,embed').forEach((el) => el.remove());
      tmp.querySelectorAll('*').forEach((el) => {
        [...el.attributes].forEach((attr) => {
          const name = attr.name.toLowerCase();
          const value = attr.value || '';
          if (name.startsWith('on') || (name === 'href' && /^\s*javascript:/i.test(value))) {
            el.removeAttribute(attr.name);
          }
        });
      });
      return tmp.innerHTML;
    }

    /**
     * Native select styled with the theme's own variant picker dropdown classes, so it matches
     * the "Koko" selector above it. Native keeps the mobile OS picker, keyboard support and
     * needs none of the theme's dropdown JavaScript.
     * @param {object} opts - { selectedHandle, withPrice, infoKey }
     */
    _selectGroupHtml(field, title, options, { selectedHandle = null, withPrice = false, infoKey = field } = {}) {
      const id = `sanky-${this.dataset.productBlock || 'cfg'}-${field}`;
      const list = options || [];
      const selected = selectedHandle || list[0]?.handle || null;

      const optionsHtml = list.map((opt) => `
        <option value="${this._esc(opt.handle)}"${opt.handle === selected ? ' selected' : ''}>
          ${this._esc(withPrice ? this._mattressOptionLabel(opt) : opt.name)}
        </option>`).join('');

      return `
        <div class="sanky-config__group sanky-config__group--select" data-field="${field}">
          ${this._labelRowHtml(title, infoKey, id)}
          <div class="sanky-config__select-wrap">
            <select
              id="${id}"
              class="variant-picker-dropdown__toggle variant-picker-dropdown__toggle--outline sanky-config__select"
              data-sanky-field="${field}"
            >
              ${optionsHtml}
            </select>
            ${CHEVRON_SVG}
          </div>
        </div>`;
    }

    // Keeps surcharges visible now that the options are collapsed into a dropdown
    _mattressOptionLabel(opt) {
      const delta = Math.round(this._jousitusPrice(opt.handle));
      if (!delta) return opt.name;
      // 160 cm+ beds pick a jousitus per side, so the amount is per side too
      const unit = this.config.pricing?.jousitus?.mode === 'side' ? ' €/puoli' : ' €';
      return `${opt.name} (${delta > 0 ? '+' : '−'}${Math.abs(delta)}${unit})`;
    }

    // Text pill group, used for the two/three-option choices (unified vs. split, massage, ...)
    _groupHtml(field, title, options, selectedHandle = null) {
      const idBase = `sanky-${this.dataset.productBlock || 'cfg'}-${field}`;
      const list = options || [];
      const selected = selectedHandle || list[0]?.handle || null;
      const items = list.map((opt) => {
        const id = `${idBase}-${opt.handle}`;
        const checked = opt.handle === selected;
        const selectedClass = checked ? 'selected' : '';
        const checkedAttr = checked ? 'checked' : '';

        return `
          <input class="visually-hidden" type="radio" name="${idBase}" id="${id}" value="${this._esc(opt.handle)}" data-sanky-field="${field}" ${checkedAttr}>
          <label for="${id}" class="pill pill--large pill--outline ${selectedClass}"${checked ? ' tabindex="0"' : ''}>
            <span class="pill__label">${this._esc(opt.name)}</span>
          </label>`;
      }).join('');

      return `
        <div class="sanky-config__group" data-field="${field}">
          ${this._labelRowHtml(title, field)}
          <div class="product-variant-picker__pill-list">${items}</div>
        </div>`;
    }

    _tieredSwatchGroupsHtml(field, title, options, pricingKey, infoKey = field) {
      const tiers = ['Core', 'Premium', 'Lux'];
      const table = this.config.pricing?.[pricingKey];
      const selected = (options || [])[0]?.handle || null;

      const heading = `
        <div class="sanky-config__selection">
          <span class="sanky-config__selection-label heading-font-family">${this._esc(title)}</span>
          ${this._infoButtonHtml(infoKey, title)}
          <span class="sanky-config__selection-value heading-font-family" data-sanky-selection="${field}"></span>
        </div>`;

      const tierGroups = tiers.map((tier) => {
        const tierOptions = (options || []).filter((o) => o.tier === tier);
        if (!tierOptions.length) return '';

        const priceLabel = this._formatTierPrice(this._tierPrice(table, tier));
        const noun = field === 'legs' ? 'jalat' : 'kangas';
        const tierTitle = `${tier}-${noun} (${priceLabel})`;

        return `
          <div class="sanky-config__tier" data-tier="${tier}">
            <div class="product-variant-picker__option-label sanky-config__tier-label">
              <span class="heading-font-family">${this._esc(tierTitle)}</span>
            </div>
            <div class="sanky-config__swatches" role="group" aria-label="${this._esc(tierTitle)}">
              ${this._swatchesHtml(field, tierOptions, selected)}
            </div>
          </div>`;
      }).join('');

      return `${heading}${tierGroups}`;
    }

    /**
     * Image/colour only swatches. The name is never printed next to the swatch — it shows up
     * in the group heading (also on hover/focus) and in the title/aria-label for a11y.
     */
    _swatchesHtml(field, options, selectedHandle) {
      const idBase = `sanky-${this.dataset.productBlock || 'cfg'}-${field}`;
      return (options || []).map((opt) => {
        const id = `${idBase}-${opt.handle}`;
        const checked = opt.handle === selectedHandle;
        const name = this._esc(opt.name);

        let style = '';
        let inner = '';
        if (opt.image) {
          const srcset = opt.imageSrcset
            ? ` srcset="${this._esc(opt.imageSrcset)}"`
            : '';
          inner = `<img class="sanky-config__swatch-img" src="${this._esc(opt.image)}"${srcset} sizes="4.4rem" width="44" height="44" alt="" decoding="async" loading="lazy">`;
        } else if (opt.color) {
          style = `background-color: ${this._esc(opt.color)};`;
        } else {
          inner = `<span class="sanky-config__swatch-initials" aria-hidden="true">${this._esc(this._initials(opt.name))}</span>`;
        }

        // The selection ring lives on the label and the colour on the inner span, so the ring
        // stays inside the swatch box and cannot be clipped at the edge of a scrolling container.
        return `
          <input class="visually-hidden sanky-config__swatch-input" type="radio" name="${idBase}" id="${id}" value="${this._esc(opt.handle)}" data-sanky-field="${field}" ${checked ? 'checked' : ''}>
          <label for="${id}" class="sanky-config__swatch" title="${name}" aria-label="${name}" data-sanky-swatch-name="${name}">
            <span class="sanky-config__swatch-color" style="${style}">${inner}</span>
          </label>`;
      }).join('');
    }

    _initials(name) {
      return String(name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase();
    }

    /**
     * Kuosi and jalat are charged once for the whole bed — the pricing master
     * already includes the second sleeping side — so no per-side label here.
     */
    _formatTierPrice(total) {
      return `+${Math.round(total) || 0} €`;
    }

    _setDefaults() {
      const rules = this._rules();
      const mattresses = this._mattressesForMallisto();
      const firstMattress = mattresses[0]?.handle || null;
      const firstFabric = this.config.fabrics[0]?.handle || null;
      const firstLegs = this.config.legs[0]?.handle || null;

      if (rules.showSingleMattress) this.state.mattress = firstMattress;
      if (rules.showSplitMattress) {
        this.state.mattressLeft = firstMattress;
        this.state.mattressRight = firstMattress;
      }
      if (rules.showFabric) this.state.fabric = firstFabric;
      if (rules.showLegs) this.state.legs = firstLegs;
      if (rules.showSpringSplit) this.state.springSplit = 'unified';
      if (rules.showTopperSplit) this.state.topperSplit = 'unified';
      if (rules.showRemote) this.state.remotePocket = 'narrow';
      if (rules.showMassageSplit) {
        this.state.massageLeft = 'none';
        this.state.massageRight = 'none';
      } else if (rules.showMassage) {
        this.state.massage = 'none';
      }
    }

    _findByHandle(list, handle) {
      return (list || []).find((x) => x.handle === handle) || null;
    }

    /**
     * Jousitus price for one mattress selection. Firmness does not affect price,
     * so the handle is mapped to its jousitus key (tasakovuinen-hard →
     * tasakovuinen) before the lookup.
     */
    _jousitusPrice(handle) {
      const jousitus = this.config.pricing?.jousitus;
      if (!handle || !jousitus) return 0;
      const key = this.config.pricing.mattresses?.[handle];
      if (!key) return 0;
      const n = Number(jousitus.prices?.[key]);
      return Number.isFinite(n) ? n : 0;
    }

    /** Kuosi and jalat tables are keyed by tier and are whole-product totals. */
    _tierPrice(table, tier) {
      if (!table || tier == null) return 0;
      const n = Number(table[tier]);
      return Number.isFinite(n) ? n : 0;
    }

    /**
     * Mirrors the Cart Transform function. Every amount from the pricing master
     * is a finished euro sum, so there are no width multipliers here: kuosi and
     * jalat already include the second sleeping side, and jousitus tables with
     * mode "side" are priced per side.
     */
    _calcTotal() {
      const rules = this._rules();
      const { basePrice } = this.config.product;
      const pricing = this.config.pricing;

      if (!pricing?.jousitus) {
        // Prices not seeded yet — show no total rather than a wrong one.
        return { base: basePrice, extras: 0, total: basePrice, available: false };
      }

      let extras = 0;

      if (pricing.jousitus.mode === 'side' && rules.showSplitMattress) {
        extras += this._jousitusPrice(this.state.mattressLeft);
        extras += this._jousitusPrice(this.state.mattressRight);
      } else {
        extras += this._jousitusPrice(this.state.mattress);
      }

      if (rules.showFabric && this.state.fabric) {
        const fabric = this._findByHandle(this.config.fabrics, this.state.fabric);
        extras += this._tierPrice(pricing.kuosi, fabric?.tier);
      }
      if (rules.showLegs && this.state.legs) {
        const legs = this._findByHandle(this.config.legs, this.state.legs);
        extras += this._tierPrice(pricing.jalat, legs?.tier);
      }

      if (pricing.hieronta?.mode === 'side') {
        const price = Number(pricing.hieronta.price) || 0;
        if (rules.showMassageSplit) {
          if (this.state.massageLeft === 'with') extras += price;
          if (this.state.massageRight === 'with') extras += price;
        } else if (rules.showMassage && this.state.massage === 'with') {
          extras += price;
        }
      }

      return {
        base: basePrice,
        extras,
        total: basePrice + extras,
        available: true,
      };
    }

    _formatMoney(amount) {
      const currency = this.config.product.currency || 'EUR';
      try {
        return new Intl.NumberFormat('fi-FI', {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(amount);
      } catch (_) {
        return `${Math.round(amount)} €`;
      }
    }

    _summaryText(rules, pricing) {
      const parts = [];
      const nameOf = (list, handle) => this._findByHandle(list, handle)?.name || handle;

      if (rules.showSingleMattress && this.state.mattress) {
        parts.push(`Patja: ${nameOf(this.config.mattresses, this.state.mattress)}`);
      }
      if (rules.showSplitMattress) {
        parts.push(`Vasen: ${nameOf(this.config.mattresses, this.state.mattressLeft)}`);
        parts.push(`Oikea: ${nameOf(this.config.mattresses, this.state.mattressRight)}`);
      }
      if (rules.showSpringSplit && this.state.springSplit) {
        parts.push(`Joustinpatja: ${this.state.springSplit === 'split' ? 'kaksi erillistä' : 'yhtenäinen'}`);
      }
      if (rules.showTopperSplit && this.state.topperSplit) {
        parts.push(`Petauspatja: ${this.state.topperSplit === 'split' ? 'kaksi erillistä' : 'yhtenäinen'}`);
      }
      if (rules.showFabric && this.state.fabric) {
        parts.push(`Kangas: ${nameOf(this.config.fabrics, this.state.fabric)}`);
      }
      if (rules.showLegs && this.state.legs) {
        parts.push(`Jalat: ${nameOf(this.config.legs, this.state.legs)}`);
      }
      if (rules.showRemote && this.state.remotePocket) {
        const remote = this._copy(rules).remoteOpts.find((o) => o.handle === this.state.remotePocket);
        parts.push(remote?.name || this.state.remotePocket);
      }
      if (rules.showMassageSplit) {
        if (this.state.massageLeft === 'with') parts.push('Hieronta vasen');
        if (this.state.massageRight === 'with') parts.push('Hieronta oikea');
        if (this.state.massageLeft !== 'with' && this.state.massageRight !== 'with') parts.push('Ei hierontaa');
      } else if (rules.showMassage && this.state.massage) {
        parts.push(this.state.massage === 'with' ? 'Hieronta' : 'Ei hierontaa');
      } else if (rules.massageIncluded) {
        parts.push('Hieronta sisältyy');
      }

      if (pricing.available) parts.push(`Yht. ${this._formatMoney(pricing.total)}`);
      return parts.join(' · ');
    }

    _sync() {
      const rules = this._rules();
      const pricing = this._calcTotal();

      const props = {
        _mattress: rules.showSingleMattress ? (this.state.mattress || '') : '',
        _mattress_left: rules.showSplitMattress ? (this.state.mattressLeft || '') : '',
        _mattress_right: rules.showSplitMattress ? (this.state.mattressRight || '') : '',
        _fabric: rules.showFabric ? (this.state.fabric || '') : '',
        _legs: rules.showLegs ? (this.state.legs || '') : '',
        _topper_split: rules.showTopperSplit ? (this.state.topperSplit || '') : '',
        _spring_split: rules.showSpringSplit ? (this.state.springSplit || '') : '',
        _remote_pocket: rules.showRemote ? (this.state.remotePocket || '') : '',
        _massage: rules.showMassageSplit
          ? ''
          : (rules.showMassage ? (this.state.massage || '') : (rules.massageIncluded ? 'included' : '')),
        _massage_left: rules.showMassageSplit ? (this.state.massageLeft || '') : '',
        _massage_right: rules.showMassageSplit ? (this.state.massageRight || '') : '',
        _config_total: pricing.available ? String(Math.round(pricing.total)) : '',
        Kokoonpano: this._summaryText(rules, pricing),
      };

      Object.entries(props).forEach(([key, value]) => {
        const input = this.querySelector(`[data-sanky-prop="${key}"]`);
        if (!input) return;
        input.value = value;
        // Empty props must not submit with the product form
        input.disabled = value === '' || value == null;
      });

      this._updateSelectionLabels();

      if (this.priceEl && this.priceValueEl && this.config.ui?.showPricePreview) {
        this.priceEl.hidden = !pricing.available;
        this.priceValueEl.textContent = pricing.available ? this._formatMoney(pricing.total) : '—';
      }

      this.dispatchEvent(new CustomEvent('sanky:change', {
        bubbles: true,
        detail: { state: { ...this.state }, pricing, rules },
      }));
    }

    disconnectedCallback() {
      this._closeInfo();
    }

    _esc(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get('sanky-configurator')) {
    customElements.define('sanky-configurator', SankyConfigurator);
  }
})();
